import { PassCategory } from "@/models.g";

// Pricing and quantity logic for the pass-type selector. Parameters are typed structurally
// so the Coalesce models and minimal test fixtures both satisfy them.

export interface SeriesEventDateLike {
  id?: number | null;
  availableSeats?: number | null;
  eventStartDateTime?: Date | string | null;
}

export interface PassDateLike {
  eventDate?: SeriesEventDateLike | null;
  // Remaining inventory for THIS pass's ticket type on this date (per-price cap). Null when the
  // price is uncapped — availability then falls back to the date's shared availableSeats.
  availableTickets?: number | null;
}

export interface PassTypeLike {
  id?: number | null;
  passCategory?: PassCategory | number | null;
  order?: number | null;
  isActive?: boolean | null;
  maxQuantityPerPurchase?: number | null;
  eventPrice?: { price?: number | null; serviceFee?: number | null } | null;
  passTypeDates?: PassDateLike[] | null;
}

export interface PassTicketLike {
  passTypeId?: number | null;
  price?: number | null;
  fees?: number | null;
  convenienceFee?: number | null;
  salesTax?: number | null;
  totalValue?: number | null;
}

export function sortedPassDates(passType: PassTypeLike): SeriesEventDateLike[] {
  return (passType.passTypeDates ?? [])
    .map((ptd) => ptd.eventDate)
    .filter((ed): ed is SeriesEventDateLike => !!ed)
    .sort(
      (a, b) =>
        new Date(a.eventStartDateTime as any).getTime() -
        new Date(b.eventStartDateTime as any).getTime(),
    );
}

export function datesPerPass(passType: PassTypeLike): number {
  return Math.max(1, sortedPassDates(passType).length);
}

export function ticketsForPass<T extends PassTicketLike>(
  passTypeId: number | null | undefined,
  tickets: T[],
): T[] {
  return tickets.filter((t) => t.passTypeId === passTypeId);
}

/** floor(matching tickets / dates-per-pass): a multi-day pass yields N tickets per pass. */
export function currentQuantity(
  passType: PassTypeLike,
  tickets: PassTicketLike[],
): number {
  return Math.floor(
    ticketsForPass(passType.id, tickets).length / datesPerPass(passType),
  );
}

// Prefer the pass's own per-price inventory over the date's shared capacity (the precedence normal
// events use). Null = untracked/uncapped, treated as available by callers.
export function passDateRemaining(passDate: PassDateLike): number | null {
  return (
    passDate.availableTickets ?? passDate.eventDate?.availableSeats ?? null
  );
}

// A pass is sold out if ANY of its covered dates has no remaining inventory — a multi-day pass needs
// a ticket on every date, so one exhausted date blocks the whole pass.
export function anyDateSoldOut(passType: PassTypeLike): boolean {
  return (passType.passTypeDates ?? []).some((ptd) => {
    const remaining = passDateRemaining(ptd);
    return remaining !== null && remaining <= 0;
  });
}

// How many more of this pass can be added, bounded by the scarcest covered date (each pass consumes
// one ticket per date). Null when no date has a tracked remaining (uncapped) — no inventory limit to show.
export function passInventoryRemaining(passType: PassTypeLike): number | null {
  const remainings = (passType.passTypeDates ?? [])
    .map(passDateRemaining)
    .filter((r): r is number => r !== null);
  if (remainings.length === 0) return null;
  return Math.max(0, Math.min(...remainings));
}

/** undefined (unlimited) when the cap is 0. */
export function maxQuantity(passType: PassTypeLike): number | undefined {
  const max = passType.maxQuantityPerPurchase ?? 0;
  return max > 0 ? max : undefined;
}

// EventPrice.price/serviceFee are whole-pass totals, not per-day.
export function unitPrice(passType: PassTypeLike): number {
  return passType.eventPrice?.price ?? 0;
}

export function unitServiceFee(passType: PassTypeLike): number {
  return passType.eventPrice?.serviceFee ?? 0;
}

export function perDayPrice(passType: PassTypeLike): number {
  return unitPrice(passType) / datesPerPass(passType);
}

export function isMultiDayPass(passType: PassTypeLike): boolean {
  return (
    passType.passCategory === PassCategory.MultiDay &&
    datesPerPass(passType) > 1
  );
}

// Sums a pass type's own tickets. Pass types can share an EventPrice, so an
// EventPrice-keyed breakdown would bleed another row's fees into this one.
export function sumPassTickets(
  passTypeId: number | null | undefined,
  tickets: PassTicketLike[],
  select: (t: PassTicketLike) => number | null | undefined,
): number {
  return ticketsForPass(passTypeId, tickets).reduce(
    (acc, t) => acc + (select(t) ?? 0),
    0,
  );
}

// Per-pass amounts for the caption under the pass name. Once any passes are in the cart we
// derive these from the priced tickets so the caption matches what is actually charged: the
// backend resolves the service fee (ResolveServiceFee) and can land on a value that differs
// from the raw EventPrice.serviceFee (waived/free events, the tenant fee snapshot winning over
// a non-overridden row). With no tickets yet we have nothing priced, so fall back to EventPrice.
export function effectiveUnitPrice(
  passType: PassTypeLike,
  tickets: PassTicketLike[],
): number {
  const qty = currentQuantity(passType, tickets);
  if (qty <= 0) return unitPrice(passType);
  return sumPassTickets(passType.id, tickets, (t) => t.price) / qty;
}

export function effectiveUnitServiceFee(
  passType: PassTypeLike,
  tickets: PassTicketLike[],
): number {
  const qty = currentQuantity(passType, tickets);
  if (qty <= 0) return unitServiceFee(passType);
  return sumPassTickets(passType.id, tickets, (t) => t.fees) / qty;
}

export function effectivePerDayPrice(
  passType: PassTypeLike,
  tickets: PassTicketLike[],
): number {
  return effectiveUnitPrice(passType, tickets) / datesPerPass(passType);
}

// Minimal view of a settled coalesce caller after a quantity update.
export interface QuantityUpdateCaller {
  wasSuccessful?: boolean | null;
  message?: string | null;
}

// The message to show after a quantity update, or null when nothing went wrong. Keys off
// wasSuccessful === false (not falsiness) so a not-yet-settled or debounce-cancelled caller
// (wasSuccessful null) shows nothing rather than flashing a spurious error.
export function quantityUpdateError(
  caller: QuantityUpdateCaller,
  fallback: string,
): string | null {
  if (caller.wasSuccessful !== false) return null;
  return caller.message ?? fallback;
}

/** Active passes, sorted by Order. */
export function activePassTypes<T extends PassTypeLike>(passTypes: T[]): T[] {
  return [...passTypes]
    .filter((pt) => pt.isActive !== false)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

// Group by the admin's declared PassCategory rather than isMultiDayPass (which also requires >1
// date), so a pass stays in the section the admin assigned even when its dates are mis-entered.
export function partitionPassTypesByCategory<T extends PassTypeLike>(
  passTypes: T[],
): { multiDay: T[]; singleDay: T[] } {
  const active = activePassTypes(passTypes);
  return {
    multiDay: active.filter((pt) => pt.passCategory === PassCategory.MultiDay),
    singleDay: active.filter((pt) => pt.passCategory !== PassCategory.MultiDay),
  };
}
